import {useMemo} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useQueries} from '@github-ui/react-query'

import {copilotChatRepositoryPath} from '../utils/api-url-helpers'

export interface RepoInfo {
  id: number
  name: string
  ownerLogin: string
}

async function fetchRepoInfo(repoID: number): Promise<RepoInfo | null> {
  try {
    const res = await verifiedFetchJSON(copilotChatRepositoryPath(repoID), {method: 'GET'})
    if (!res.ok) return null
    const payload = await res.json()
    return {
      id: payload.id,
      name: payload.name,
      ownerLogin: payload.owner?.login ?? payload.ownerLogin,
    }
  } catch {
    return null
  }
}

/**
 * Hook to fetch repository info for a set of repository IDs.
 * Returns a map of repository ID to RepoInfo, loading state, and error state.
 */
export function useRepoInfo(repoIds: number[]) {
  // Deduplicate and sort IDs for stable query keys
  const uniqueIds = useMemo(() => [...new Set(repoIds)].sort((a, b) => a - b), [repoIds])

  const queries = useQueries({
    queries: uniqueIds.map(repoId => ({
      queryKey: ['repo-info', repoId],
      queryFn: () => fetchRepoInfo(repoId),
      staleTime: Infinity, // No need to refetch repo info more frequently than on page load
    })),
  })

  // Extract stable values from queries to use in dependencies
  const queryData = queries.map(q => q.data)

  // Build the repo map from query results
  const repoMap = useMemo(() => {
    const map: Record<number, RepoInfo> = {}
    for (const [index, data] of queryData.entries()) {
      const repoId = uniqueIds[index]
      if (data && repoId !== undefined) {
        map[repoId] = data
      }
    }
    return map
  }, [queryData, uniqueIds])

  const isLoading = queries.some(q => q.isLoading)
  const isError = queries.some(q => q.isError)

  return {data: repoMap, isLoading, isError}
}
